import { ArrowLeft } from "lucide-react";
import CaseStudyVideo from "../components/content/CaseStudyVideo";
import {
  BuildBadge,
  CaseQuote,
  Modules,
  StatBand,
} from "../components/results/CaseParts";
import ProjectCard from "../components/results/ProjectCard";
import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import { ButtonLink } from "../components/ui/Button";
import SectionHeading from "../components/ui/SectionHeading";
import { pageTitle } from "../content";
import { images } from "../data/images";
import {
  canonicalCasePath,
  caseDetailFor,
  caseStudyDetails,
  caseTradeFor,
  industryLabel,
  moreProjects,
  resultsFor,
  storyCta,
  tradeLabel,
} from "../data/results";
import { routes } from "../data/site";
import { blocksBefore } from "../lib/content-blocks";
import { cx } from "../lib/cx";
import { base, localHref, photoProps } from "../lib/links";
import { isYouTubeUrl } from "../lib/youtube";
import CaseStudySections from "./CaseStudySections";

// Captured "related posts" trailers; the page renders its own related cards.
const trailingSections =
  /Other Team Members|RELATED|Related Posts|The True Cost of Generic/;

/** Captured body without trailers or the h2 that repeats the title. */
function capturedBlocks(page, title) {
  const blocks = blocksBefore(page.body || [], trailingSections);
  return blocks[0]?.type === "h2" && blocks[0].text === title
    ? blocks.slice(1)
    : blocks;
}

const resultsHref = (trade) =>
  localHref(routes.caseStudies) + (trade ? "#" + trade : "");

/** Every case study in card form: headline stories first. */
const allProjects = [
  ...Object.entries(caseStudyDetails).map(([path, study]) => [
    path,
    {
      trade: study.trade,
      industry: study.industry,
      company: study.company,
      title: `What we built: ${study.build}`,
      summary: study.runningNow[0],
      photo: study.photo,
    },
  ]),
  ...Object.entries(moreProjects),
];

/** Three other case studies, same trade first. */
function relatedTo(path) {
  const trade = caseTradeFor(path);
  const others = allProjects.filter(([p]) => p !== path);
  return [
    ...others.filter(([, p]) => p.trade === trade),
    ...others.filter(([, p]) => p.trade !== trade),
  ].slice(0, 3);
}

function BackLink({ trade, className }) {
  return (
    <a
      href={resultsHref(trade)}
      className={cx(
        "inline-flex items-center gap-2 font-bold transition-colors",
        className,
      )}
    >
      <ArrowLeft size={17} aria-hidden="true" /> Back to Results
    </a>
  );
}

/** Small cobalt label led by a yellow bar (the mockup's story heads). */
function StoryHeading({ children }) {
  return (
    <h2 className="eyebrow mt-12 mb-4 text-[15px] font-semibold text-blue-deep first:mt-0">
      {children}
    </h2>
  );
}

function Photo({ photo, sizes = "(max-width: 1024px) 100vw, 720px" }) {
  return (
    <figure className="my-9">
      <img
        {...photoProps(photo.src, sizes)}
        alt={photo.alt}
        loading="lazy"
        className="aspect-video w-full rounded-brand object-cover shadow-soft"
      />
    </figure>
  );
}

function Screenshot({ shot }) {
  return (
    <figure className={cx("my-9", shot.phone && "mx-auto max-w-70")}>
      <a
        href={photoProps(shot.src).src}
        target="_blank"
        rel="noopener noreferrer"
        className="block overflow-hidden rounded-brand border border-line bg-white p-2 shadow-soft transition-shadow hover:shadow-lift"
      >
        <img
          {...photoProps(
            shot.src,
            shot.phone ? "280px" : "(max-width: 1024px) 100vw, 720px",
          )}
          alt={shot.alt}
          loading="lazy"
          className="h-auto w-full rounded-md"
        />
        <span className="sr-only">(opens full size in a new tab)</span>
      </a>
      <figcaption className="mt-3 text-sm text-muted">
        {shot.caption}
      </figcaption>
    </figure>
  );
}

function Aside({ study }) {
  const facts = [
    ["Industry", industryLabel(study)],
    ["Client", study.company],
    ["What we built", study.build],
  ];
  return (
    <aside className="flex flex-col gap-5 lg:sticky lg:top-24">
      <div className="rounded-brand border border-line bg-white p-6 shadow-soft">
        {study.logo && (
          <div className="mb-6 h-20 overflow-hidden">
            <img
              {...photoProps(study.logo, "240px")}
              alt={`${study.company} logo`}
              loading="lazy"
              className={cx(
                "h-full w-auto max-w-full rounded-lg object-contain",
                study.logoPadded && "aspect-square scale-[1.8]",
              )}
            />
          </div>
        )}
        <dl className="grid gap-4">
          {facts.map(([term, value]) => (
            <div key={term}>
              <dt className="tag text-[11.5px] font-semibold text-muted">
                {term}
              </dt>
              <dd className="mt-0.5 font-bold text-ink">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="blueprint rounded-brand p-6 text-white">
        <p className="font-display text-[22px] leading-tight uppercase">
          Got a headache <span className="text-yellow">like this one?</span>
        </p>
        <p className="mt-2 mb-5 text-[15px] text-white/80">
          Sixty minutes, no pitch deck. You talk, we listen.
        </p>
        <ButtonLink size="md" className="w-full" />
      </div>
    </aside>
  );
}

/** The marketing "Stories" layout for the headline case studies. */
function Story({ study, videos, title }) {
  const { story, photos = [] } = study;
  const screenshots =
    study.screenshots || (study.screenshot ? [study.screenshot] : []);
  return (
    <section className="bg-white px-4 py-14 sm:px-6 md:py-20">
      <div className="mx-auto grid max-w-280 grid-cols-1 items-start gap-12 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-16">
        <article className="min-w-0 text-[17px] leading-[1.75] text-slate">
          <StoryHeading>The situation</StoryHeading>
          {story.situation.map((text) => (
            <p key={text} className="mb-4">
              {text}
            </p>
          ))}
          {photos[0] && <Photo photo={photos[0]} />}

          <StoryHeading>{story.builtHeading}</StoryHeading>
          <Modules items={study.modules} className="mt-1 mb-5" />
          {[story.built].flat().map((text) => (
            <p key={text} className="mb-4 last:mb-0">
              {text}
            </p>
          ))}
          {screenshots.length
            ? screenshots.map((shot) => (
                <Screenshot key={shot.src} shot={shot} />
              ))
            : photos[1] && <Photo photo={photos[1]} />}

          <StoryHeading>Running now</StoryHeading>
          <p>{story.runningNow}</p>
          {screenshots.length > 0 && photos[1] && <Photo photo={photos[1]} />}

          {study.stats.length > 0 && (
            <StatBand
              stats={study.stats}
              compact
              className="my-9 overflow-hidden rounded-brand"
            />
          )}
          {study.quote && <CaseQuote quote={study.quote} large />}
          <BackLink
            trade={study.trade}
            className="mt-10 border-b-2 border-yellow pb-0.5 text-blue-deep hover:text-ink"
          />
        </article>
        <Aside study={study} />
      </div>
      {videos.length > 0 && <Videos videos={videos} title={title} />}
    </section>
  );
}

function Videos({ videos, title }) {
  return (
    <div className="mx-auto mt-16 max-w-280 border-t border-line pt-14 md:mt-20 md:pt-16">
      <SectionHeading
        eyebrow="See it in action"
        title="Watch the *story.*"
        className="mb-8"
      />
      <div
        className={cx(
          "grid grid-cols-1 gap-6",
          videos.length > 1 ? "md:grid-cols-2" : "max-w-3xl",
        )}
      >
        {videos.map((url, i) => (
          <CaseStudyVideo
            key={url}
            url={url}
            title={`${title} video ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function Related({ path }) {
  return (
    <section className="bg-surface px-4 py-16 sm:px-6 md:py-20">
      <div className="mx-auto max-w-280">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            eyebrow="More results"
            title="Companies like *yours.*"
          />
          <a
            href={localHref(routes.caseStudies)}
            className="border-b-2 border-yellow pb-0.5 font-bold text-blue-deep transition-colors hover:text-ink"
          >
            See all results →
          </a>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {relatedTo(path).map(([href, project]) => (
            <ProjectCard key={href} path={href} project={project} />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * A case study. The five headline stories use the marketing layout; the
 * rest keep their captured write-ups. Paginated duplicates (/slug/2/)
 * render as the case study itself.
 */
export default function CaseStudyPage({ page, path }) {
  const canonical = canonicalCasePath(path);
  const study = caseDetailFor(path);
  const trade = caseTradeFor(path);
  const videos = page.externalEmbeds?.filter(isYouTubeUrl) || [];
  const title = study ? study.company : pageTitle(page);
  // The page's own photo when it was downloaded, else the jobsite still.
  const ownImage = page.image && localHref(page.image);
  const heroImage = study
    ? study.photo
    : ownImage?.startsWith(base)
      ? ownImage
      : images.heroJobsite;

  return (
    <>
      <PageHero
        eyebrow={
          study
            ? `Case study · ${industryLabel(study)}`
            : trade
              ? `Case study · ${tradeLabel(trade)}`
              : "Case study"
        }
        title={study ? study.story.title : title}
        lede={study?.story.who}
        image={heroImage}
        imageAlt={study?.photoAlt || ""}
        long
      >
        {study && (
          <div className="flex flex-wrap items-center gap-4">
            <BuildBadge>{study.build}</BuildBadge>
            <BackLink
              trade={trade}
              className="text-[15px] text-white/85 hover:text-yellow"
            />
          </div>
        )}
      </PageHero>

      {study ? (
        <Story study={study} videos={videos} title={title} />
      ) : (
        <CaseStudySections
          blocks={capturedBlocks(page, title)}
          title={title}
          results={resultsFor(path)}
          videos={videos}
          path={path}
          backHref={resultsHref(trade)}
        />
      )}

      <Related path={canonical} />
      <CtaPanel
        title={storyCta.title}
        text={storyCta.text}
        className="bg-white"
      />
    </>
  );
}
